import type { ElectricSpecs } from '../api/types'

/**
 * The electric section's heading.
 *
 * Plain, and deliberately not "Accu en laden volgens EVDB". The attribution the licence requires is
 * discharged in full by `SourceNote` at the foot of the card — creator, licence and a link to it —
 * so the heading does not have to carry a second copy of the credit. CC BY-SA 4.0 §3(a)(2) lets the
 * notices be given "in any reasonable manner based on the medium, means and context", which is what
 * makes one clear source note enough and a shouty heading unnecessary.
 *
 * It also keeps this readable where the title is reused as a plain label: the jump-to chips and the
 * premium banner's enumeration both print it inline.
 */
export const ELECTRIC_SECTION_TITLE = 'Accu en laden'

export function electricSectionSubtitle(electric: ElectricSpecs): string {
  const variant = electric.modelYear
    ? `${electric.matchedVariant} (${electric.modelYear})`
    : electric.matchedVariant

  return `Gegevens van de ${variant}`
}

/**
 * The driven wheels, in Dutch.
 *
 * The catalogue writes these as English abbreviations. An unrecognised value is passed through
 * rather than dropped or guessed at — a code we have not seen is still more informative than
 * nothing, and inventing a translation for it would be worse than either.
 */
export function formatDriveType(value: string | undefined): string | undefined {
  if (!value) {
    return undefined
  }

  switch (value.toUpperCase()) {
    case 'FWD':
      return 'Voorwielaandrijving'
    case 'RWD':
      return 'Achterwielaandrijving'
    case 'AWD':
    case '4WD':
      return 'Vierwielaandrijving'
    default:
      return value
  }
}

/**
 * The battery warranty, which the catalogue publishes as two independent numbers.
 *
 * A manufacturer's warranty runs to whichever limit is reached first, so the two belong in one
 * line — "8 jaar of 160.000 km" — and either alone is still worth showing.
 */
export function formatBatteryWarranty(
  years: number | undefined,
  kilometres: number | undefined,
  formatKilometres: (value: number) => string,
): string | undefined {
  const yearPart = years === undefined ? undefined : `${years} jaar`
  const kmPart = kilometres === undefined ? undefined : formatKilometres(kilometres)

  if (yearPart && kmPart) {
    return `${yearPart} of ${kmPart}`
  }

  return yearPart ?? kmPart
}
